import { useState, type PropsWithChildren, type ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@shared-kernel/primary/shared/shadcn/ui/card";
import { Separator } from "@shared-kernel/primary/shared/shadcn/ui/separator";
import { Collapsible, CollapsibleTriggerWithHover, CollapsibleContent } from "@shared-kernel/primary/shared/shadcn/ui/collapsible";
import { cn } from "@components/utils/utils";

interface Props {
  title: string;
  headerChildren?: ReactNode;
  cardClassname?: string;
  headerChildrenPosition?: "right" | "below";
  defaultExpanded?: boolean;
}

export const CollapsibleCustomCard = ({
  title,
  children,
  headerChildren,
  cardClassname,
  headerChildrenPosition = "right",
  defaultExpanded = true,
}: PropsWithChildren<Props>) => {
  const [isOpen, setIsOpen] = useState(defaultExpanded);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className={cn("w-full", cardClassname)}>
        <CollapsibleTriggerWithHover asChild isOpen={isOpen}>
          <CardHeader
            className={cn("flex cursor-pointer", {
              "flex-row items-center justify-between": headerChildrenPosition === "right",
              "flex-col items-start gap-2": headerChildrenPosition === "below",
            })}
          >
            <CardTitle>{title}</CardTitle>
            <div className="flex items-center gap-2">
              {headerChildren}
              <ChevronRight className={cn("size-5 text-muted-foreground transition-transform duration-200", isOpen && "rotate-90")} />
            </div>
          </CardHeader>
        </CollapsibleTriggerWithHover>
        <CollapsibleContent>
          <Separator />
          <CardContent className="flex flex-col items-center pt-3 text-foreground">{children}</CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};
